import { Link } from 'react-router-dom';
import { orderStatusLabelPtBr } from '../utils/orderStatus.js';

/** Prévias ilustrativas do painel + iframe da loja real de demonstração */

function FakeShell({ title, children, tint = 'purple' }) {
  const ring =
    tint === 'purple'
      ? 'from-purple-600/35 via-transparent to-teal-500/35'
      : 'from-accent/35 via-transparent to-teal-500/35';
  return (
    <div className="overflow-hidden rounded-2xl border border-ink/10 bg-white/90 shadow-xl transition-shadow duration-300 hover:shadow-lg dark:border-white/[0.07] dark:bg-night-elevated/95 dark:shadow-night-card dark:hover:shadow-night-card-hover">
      <div className={`flex items-center justify-between gap-3 border-b border-ink/5 bg-gradient-to-r ${ring} px-4 py-2.5 dark:border-white/[0.06]`}>
        <span className="text-[11px] font-bold uppercase tracking-[0.35em] text-ink/50 dark:text-frost-muted">{title}</span>
        <span className="hidden text-[10px] font-medium text-ink/40 sm:inline dark:text-frost-muted">só ilustração</span>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

export function LandingPreviewSection() {
  return (
    <section id="preview" className="scroll-mt-28 border-y border-ink/5 px-4 py-24 dark:border-white/[0.05] dark:bg-night-deep/40">
      <div className="mx-auto max-w-6xl">
        <div className="mx-auto mb-14 max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold sm:text-4xl dark:text-frost">Veja o painel e uma loja de verdade funcionando</h2>
          <p className="mt-3 text-ink/70 dark:text-frost-muted">
            Abaixo, exemplos visuais do que você usa no dia a dia; ao lado, a loja de demonstração deste site em{' '}
            <strong className="text-ink dark:text-frost">/</strong>
            <code className="mx-1 rounded-md bg-accent/15 px-1.5 py-0.5 text-sm text-accent dark:text-emerald-200">lojateste</code>
          </p>
          <Link
            to="/lojateste"
            className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-accent px-6 py-3 text-sm font-bold uppercase tracking-wide text-white shadow-lg shadow-accent/35 transition-transform hover:-translate-y-0.5 hover:shadow-glow"
          >
            Abrir loja demo ao vivo →
          </Link>
          <p className="mt-4 text-xs text-ink/50 dark:text-frost-muted">
            Na sua conta, o cliente acessa um endereço parecido: o nome curto que você escolher ao criar o negócio (por
            exemplo <code className="font-mono">/minhapadaria</code>).
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-[1fr_1.15fr]">
          <div className="flex flex-col gap-6">
            <FakeShell title="Pedidos — como aparecem para você" tint="purple">
              <div className="space-y-2">
                {[
                  { id: '#1042', s: 'pending', c: 'Marina L.', t: 'R$ 187,40' },
                  { id: '#1041', s: 'paid', c: 'João P.', t: 'R$ 64,90' },
                  { id: '#1040', s: 'sent_to_erp', c: 'Nina & Cia', t: 'R$ 312,00' },
                ].map((row) => (
                  <div
                    key={row.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-ink/5 bg-black/[0.02] px-3 py-2.5 text-sm transition-colors hover:border-emerald-500/20 dark:border-white/[0.06] dark:bg-night-ridge/60 dark:hover:border-emerald-400/25"
                  >
                    <span className="font-mono text-xs text-ink/45 dark:text-frost-muted">{row.id}</span>
                    <span className="font-medium text-ink dark:text-frost">{row.c}</span>
                    <span className="text-xs font-semibold text-accent2">{orderStatusLabelPtBr(row.s)}</span>
                    <span className="ml-auto font-display text-base text-accent">{row.t}</span>
                  </div>
                ))}
              </div>
            </FakeShell>

            <FakeShell title="Produtos — vitrine e preços">
              <div className="overflow-hidden rounded-xl border border-ink/5 dark:border-white/[0.06]">
                <div className="grid grid-cols-[1fr_100px_80px] gap-2 border-b border-ink/5 bg-black/[0.03] px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-ink/45 dark:border-white/[0.06] dark:bg-night-deep/80 dark:text-frost-muted">
                  <span>Nome</span>
                  <span>Seção</span>
                  <span className="text-right">Preço</span>
                </div>
                {[
                  ['Blend moído 250g', 'Bebidas', 'R$ 32,00'],
                  ['Pizza congelada M', 'Refeições', 'R$ 38,00'],
                  ['Brownie belga', 'Doces', 'R$ 18,00'],
                ].map(([n, sec, p]) => (
                  <div
                    key={n}
                    className="grid grid-cols-[1fr_100px_80px] gap-2 border-b border-ink/5 px-3 py-2 text-xs transition-colors last:border-0 hover:bg-black/[0.02] dark:border-white/[0.05] dark:hover:bg-white/[0.03]"
                  >
                    <span className="font-medium text-ink dark:text-frost">{n}</span>
                    <span className="text-ink/55 dark:text-frost-muted">{sec}</span>
                    <span className="text-right font-display text-accent">{p}</span>
                  </div>
                ))}
              </div>
            </FakeShell>

            <FakeShell title="Clientes — histórico e contato">
              <ul className="space-y-2 text-sm">
                {[
                  'Telefone e e-mail num só lugar, sem cadastro duplicado.',
                  'Últimos pedidos e endereços quando o cliente compra de novo.',
                ].map(
                  (t) => (
                    <li
                      key={t}
                      className="rounded-lg border border-dashed border-ink/15 px-3 py-2 transition-colors hover:border-emerald-400/30 dark:border-white/[0.12] dark:text-frost-muted dark:hover:text-frost"
                    >
                      {t}
                    </li>
                  )
                )}
              </ul>
            </FakeShell>
          </div>

          <div className="flex flex-col gap-5">
            <div className="relative overflow-hidden rounded-[1.75rem] border-2 border-accent/35 bg-black/[0.04] shadow-2xl transition-shadow duration-300 hover:shadow-glow dark:border-emerald-500/35 dark:bg-night-elevated/50">
              <div className="absolute inset-x-0 top-0 z-[1] flex items-center gap-2 border-b border-white/15 bg-black/65 px-4 py-3 text-[11px] font-semibold text-white backdrop-blur-md">
                <span className="inline-block h-3 w-3 rounded-full bg-red-400/95" aria-hidden />
                <span className="inline-block h-3 w-3 rounded-full bg-amber-400/95" aria-hidden />
                <span className="inline-block h-3 w-3 rounded-full bg-green-400/95" aria-hidden />
                <span className="ml-2 font-mono text-white/85">/</span>
                <span className="font-mono font-bold text-accent2">lojateste</span>
                <span className="ml-auto text-emerald-200/95">Ao vivo neste domínio</span>
              </div>
              <iframe title="Demonstração da loja pública lojateste" src="/lojateste" className="mt-[44px] h-[620px] w-full rounded-b-[1.65rem]" />
            </div>
            <p className="text-center text-[11px] text-ink/45 dark:text-frost-muted">
              É a mesma ideia do link que seus clientes vão abrir no dia a dia: uma página sua, neste domínio, com a loja
              carregando por dentro.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
